import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useIdleTimer } from "@/hooks/useIdleTimer";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const IDLE_WARNING_AFTER_MS = 25 * 60 * 1000; // warn at 25 min idle
const COUNTDOWN_SECONDS = 60; // then 60s to respond before auto sign-out

export function IdleSessionGuard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [warningOpen, setWarningOpen] = React.useState(false);
  const [secondsLeft, setSecondsLeft] = React.useState(COUNTDOWN_SECONDS);
  const countdownRef = React.useRef<number | undefined>(undefined);

  const { reset } = useIdleTimer({
    idleMs: IDLE_WARNING_AFTER_MS,
    onIdle: () => setWarningOpen(true),
  });

  const endSession = React.useCallback(async () => {
    setWarningOpen(false);
    await logout();
    navigate("/unauthorized", { replace: true });
  }, [logout, navigate]);

  const staySignedIn = React.useCallback(() => {
    setWarningOpen(false);
    reset();
  }, [reset]);

  React.useEffect(() => {
    if (!warningOpen) {
      setSecondsLeft(COUNTDOWN_SECONDS);
      return;
    }
    countdownRef.current = window.setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          window.clearInterval(countdownRef.current);
          endSession();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => window.clearInterval(countdownRef.current);
  }, [warningOpen, endSession]);

  return (
    <Dialog open={warningOpen} onOpenChange={(open) => !open && staySignedIn()}>
      <DialogContent hideClose>
        <DialogHeader>
          <DialogTitle>Still there?</DialogTitle>
          <DialogDescription>
            You&apos;ve been inactive for a while. For security, you&apos;ll be signed out in {secondsLeft}s.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={endSession}>
            Sign out now
          </Button>
          <Button onClick={staySignedIn}>Stay signed in</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
